import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

import getUserSession from '@/utils/getUserSession'

const WS_ENDPOINT = '/notification-gateway/ws'
const NOTIFICATION_DESTINATION = '/user/queue/notifications'

let stompClient
let notificationSubscription
let liveNotificationHandler
let connectedHandler

function getNotificationSocketUrl() {
  return new URL(
    WS_ENDPOINT,
    process.env.VUE_APP_DOMAIN_URL
  ).toString()
}

function handleNotificationMessage(message) {
  try {
    const notification = JSON.parse(message.body)

    if (!liveNotificationHandler) return

    Promise.resolve(
      liveNotificationHandler(notification)
    ).catch(error => {
      console.error(
        'Failed to handle live notification:',
        error
      )
    })
  }
  catch (error) {
    console.error(
      'Failed to parse live notification:',
      error
    )
  }
}

function handleConnected() {
  if (!connectedHandler) return

  Promise.resolve(
    connectedHandler()
  ).catch(error => {
    console.error(
      'Failed to handle notification connection:',
      error
    )
  })
}

function createNotificationSocketClient() {
  const client = new Client({
    webSocketFactory: () => new SockJS(
      getNotificationSocketUrl()
    ),
    reconnectDelay: 5000,
    connectionTimeout: 10000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000
  })

  client.beforeConnect = async () => {
    const { token } = await getUserSession()

    if (!token) {
      throw new Error(
        'Cannot connect to notification WebSocket '
                + 'without an authentication token'
      )
    }

    client.connectHeaders = {
      Authorization: `Bearer ${ token }`
    }
  }

  client.onConnect = () => {
    notificationSubscription = client.subscribe(
      NOTIFICATION_DESTINATION,
      handleNotificationMessage
    )

    handleConnected()
  }

  client.onStompError = frame => {
    console.error(
      'Notification STOMP error:',
      frame.headers.message,
      frame.body
    )
  }

  client.onWebSocketError = error => {
    console.error(
      'Notification WebSocket error:',
      error
    )
  }

  client.onWebSocketClose = () => {
    notificationSubscription = undefined
  }

  return client
}

export function initializeNotificationSocket({
  onConnect,
  onNotification
} = {}) {
  if (typeof onConnect === 'function') {
    connectedHandler = onConnect
  }

  if (typeof onNotification === 'function') {
    liveNotificationHandler = onNotification
  }

  if (!stompClient) {
    stompClient = createNotificationSocketClient()
  }

  if (!stompClient.active) {
    stompClient.activate()
  }

  return stompClient
}

export async function deactivateNotificationSocket() {
  if (!stompClient?.active) return

  if (
    notificationSubscription
        && stompClient.connected
  ) {
    notificationSubscription.unsubscribe()
  }

  notificationSubscription = undefined
  liveNotificationHandler = undefined
  connectedHandler = undefined

  await stompClient.deactivate()
}

export function getNotificationSocketClient() {
  return stompClient
}
